import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "context/AuthProvider";
import { fetchLocationById } from "services/locationService";
import { getLocationRankings } from "services/rankingService";
import { fetchUsers } from "services/userService";
import { canManage } from "services/tournamentService";
import PlayerRankings from "Components/Tournament/PlayerRankings";
import ExportLocationRankings from "Components/Tournament/ExportLocationRankings";
import LocationRankingUpdater from "Components/Admin/LocationRankingUpdater";
import { ArrowLeft } from "lucide-react";
import * as Sentry from "@sentry/react";

const RankingAdminPage = () => {
  const { locationId } = useParams();
  const { user } = useAuth();
  const [location, setLocation] = useState(null);
  const [locationRankings, setLocationRankings] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [fetchedLocation, fetchedRankings, fetchedUsers] =
          await Promise.all([
            fetchLocationById(locationId),
            getLocationRankings(locationId),
            fetchUsers(),
          ]);

        setLocation(fetchedLocation);
        setLocationRankings(fetchedRankings);
        setUsers(fetchedUsers);
      } catch (error) {
        Sentry.captureException(error);
        console.error("Failed to load ranking admin data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [locationId]);

  if (loading) {
    return (
      <div className="text-white text-center pt-20">Chargement...</div>
    );
  }

  if (!location) {
    return (
      <div className="text-white text-center pt-20">Lieu introuvable.</div>
    );
  }

  const isAdmin = canManage(user, location?.id);

  if (!isAdmin) {
    return (
      <div className="text-white text-center pt-20">Accès non autorisé.</div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-slate-950 text-slate-50 p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        <Link
          to={`/locations/${locationId}`}
          className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-200 transition"
        >
          <ArrowLeft size={18} />
          Retour
        </Link>

        <h1 className="text-2xl font-bold">
          Gestion des classements — {location.name}
        </h1>

        <div className="space-y-2">
          <h2 className="text-lg font-semibold text-slate-300">Classement</h2>
          <PlayerRankings
            locationRankings={locationRankings}
            isAdmin={isAdmin}
          />
        </div>

        <div className="flex flex-row gap-2">
          <ExportLocationRankings locationId={locationId} />
        </div>

        <LocationRankingUpdater
          locationId={locationId}
          locationRankings={locationRankings}
          users={users}
        />
      </div>
    </div>
  );
};

export default RankingAdminPage;
